import { z } from "zod";

/**
 * Shared DTO cho Admin Consensus/Voting Oversight (US-AE5.F5-0092 · @UC9143) — giám sát phiên biểu
 * quyết khoản chi xuyên quỹ/nhóm (READ-ONLY). Một nguồn sự thật cho api (S01 — query + aggregate
 * yes/no) và admin web (S03 — render), tránh drift shape ở 2 nơi (DRY). Mirror style admin-collections.ts.
 *
 * BẢO MẬT (invariants — LOAD-BEARING):
 * - REDACTION danh tính: DTO KHÔNG BAO GIỜ khai báo `Vote.groupMemberId` / danh tính từng người bỏ
 *   phiếu — CHỈ aggregate `yes`/`no` (int ≥ 0). `.strict()` reject mọi field lạ (gồm groupMemberId)
 *   tại biên. 4 vai admin xem CÙNG governance metadata (không redact aggregate).
 * - B1: nếu surface `expenseAmountMinor` → BigInt minor-units serialize STRING (không float/number JS).
 *   NULLABLE + OPTIONAL (join miss / expense không số).
 * - C3: schema `.strict()` — KHÔNG khai báo secret / danh tính vote; field lạ bị reject tại biên.
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 * - enum status/mode = ĐÚNG schema (`Consensus.status` open|passed|failed|expired ·
 *   `Consensus.mechanism` majority|ratio|admin_only) — KHÔNG bịa giá trị.
 *
 * Envelope khớp quy ước list repo: `{ data, pageInfo: { hasNext, nextCursor } }`.
 */

/** Max page-size. Default (20) do backend áp khi vắng (mirror admin-collections). */
export const ADMIN_CONSENSUS_MAX_LIMIT = 50;

/** Trạng thái phiên — đúng schema `Consensus.status` (open|passed|failed|expired). */
export const adminConsensusStatusSchema = z.enum(["open", "passed", "failed", "expired"]);

export type AdminConsensusStatus = z.infer<typeof adminConsensusStatusSchema>;

/**
 * Cơ chế quyết — đúng schema `Consensus.mechanism`. US-E17.F3-0190 đã thêm `silent` là mechanism THẬT
 * ⇒ oversight PHẢI nhận (trước đây phiên silent làm mapper ném ở biên). Widening — 0 regress.
 */
export const adminConsensusModeSchema = z.enum(["majority", "ratio", "admin_only", "silent"]);

export type AdminConsensusMode = z.infer<typeof adminConsensusModeSchema>;

/**
 * US-E17.F6-0193-S03 (@UC9270) — loại chủ thể phiên: `expense` (quyết định TIỀN) | `governance`
 * (PHI-TIỀN: đổi quy chế / bầu chủ nhóm / đổi cấu hình — KHÔNG BAO GIỜ post ledger).
 */
export const adminConsensusSubjectTypeSchema = z.enum(["expense", "governance"]);

export type AdminConsensusSubjectType = z.infer<typeof adminConsensusSubjectTypeSchema>;

/** Loại quyết định governance — null với phiên expense. */
export const adminGovernanceKindSchema = z.enum([
  "rule_change",
  "owner_transfer",
  "config_change",
]);

export type AdminGovernanceKind = z.infer<typeof adminGovernanceKindSchema>;

/**
 * `expenseAmountMinor` = BigInt minor-units → string (B1, no float) HOẶC null (join miss / expense
 * không số). regex chỉ nhận chuỗi số nguyên (dấu âm cho phép) — reject float `"123.45"`.
 */
const expenseAmountMinorString = z
  .string()
  .regex(/^-?\d+$/, "expenseAmountMinor phải là số nguyên minor units (không float)")
  .nullable()
  .optional();

/**
 * Query list (AC2 filter + cursor): `fundId?` (uuid), `status?` (open|passed|failed|expired),
 * `mode?` (majority|ratio|admin_only), cursor opaque optional, limit ∈ [1,50] optional. Vắng filter
 * ⇒ list toàn bộ theo cursor. Validate tại biên (Zod) — single source api ↔ admin.
 */
export const adminConsensusQuerySchema = z.object({
  fundId: z.string().uuid().optional(),
  status: adminConsensusStatusSchema.optional(),
  mode: adminConsensusModeSchema.optional(),
  /**
   * US-E17.F6-0193-S03 (@UC9270): lọc theo loại phiên (expense | governance). Vắng ⇒ TRẢ CẢ HAI
   * (list hợp nhất — admin thấy đủ bức tranh quyết định của nhóm).
   */
  subjectType: adminConsensusSubjectTypeSchema.optional(),
  /**
   * US-AE5.F7-0157 (@UC9166): full-text search bỏ dấu trên tên khoản chi liên kết (`expense.name`) —
   * biểu quyết không có title riêng. Server fold qua `public.expenses_search_norm`. Optional (default
   * undefined ⇒ KHÔNG đổi hành vi list cũ). ≤256 ký tự. READ-ONLY.
   */
  q: z.string().max(256).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(ADMIN_CONSENSUS_MAX_LIMIT).optional(),
});

export type AdminConsensusQuery = z.infer<typeof adminConsensusQuerySchema>;

/**
 * List item (AC1). `expenseName`/`expenseAmountMinor`/`fundName`/`groupName`/`deadline` NULLABLE+
 * OPTIONAL (join miss / schema null — deadline null với admin_only). `yes`/`no` = aggregate int ≥ 0.
 * KHÔNG BAO GIỜ chứa `groupMemberId` / danh tính vote / secret (`.strict()`).
 */
export const adminConsensusItemSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    /**
     * US-E17.F6-0193-S03 (@UC9270): NULLABLE — phiên `governance` KHÔNG gắn khoản chi (phi-tiền).
     * Phiên `expense` luôn có (shape enforce bằng CHECK `consensus_subject_shape_check` ở DB).
     */
    expenseId: z.string().nullable(),
    expenseName: z.string().nullable().optional(),
    expenseAmountMinor: expenseAmountMinorString,
    /** NULLABLE — governance thuộc NHÓM, không thuộc quỹ (không có dòng tiền). */
    fundId: z.string().nullable(),
    fundName: z.string().nullable().optional(),
    groupId: z.string(),
    groupName: z.string().nullable().optional(),
    /** expense | governance (@UC9270). */
    subjectType: adminConsensusSubjectTypeSchema,
    /** rule_change | owner_transfer | config_change — null với phiên expense. */
    governanceKind: adminGovernanceKindSchema.nullable(),
    /** Tiêu đề phiên governance (null với expense — dùng expenseName). KHÔNG chứa payload thô. */
    title: z.string().nullable(),
    mode: adminConsensusModeSchema,
    yes: z.number().int().min(0),
    no: z.number().int().min(0),
    deadline: z.string().nullable().optional(),
    status: adminConsensusStatusSchema,
    createdAt: z.string(),
  })
  .strict();

export type AdminConsensusItem = z.infer<typeof adminConsensusItemSchema>;

/** Envelope khớp quy ước repo `{ data, pageInfo }` (cursor opaque, hasNext). */
export const adminConsensusResponseSchema = z.object({
  data: z.array(adminConsensusItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export type AdminConsensusResponse = z.infer<typeof adminConsensusResponseSchema>;

/**
 * Detail read-only (US-AE-IMP2-T11-S5 · @UC9155) — GET /admin/consensus/sessions/:id. Mở rộng list
 * item với aggregate BỔ SUNG — TUYỆT ĐỐI KHÔNG danh tính người bỏ phiếu (LOAD-BEARING redaction):
 * - `abstain` = số phiếu trắng (aggregate int ≥ 0) — CHỈ đếm, KHÔNG ai.
 * - `totalVotes` = tổng phiếu (yes+no+abstain) — aggregate.
 * - `eligibleCount` = số thành viên đủ điều kiện (aggregate) — KHÔNG danh sách.
 * `.strict()` reject mọi field lạ (gồm groupMemberId/vote list). KHÔNG select/trả Vote.groupMemberId.
 */
export const adminConsensusDetailSchema = adminConsensusItemSchema
  .extend({
    /** Phiếu trắng (aggregate) — KHÔNG cộng vào yes/no. KHÔNG danh tính. */
    abstain: z.number().int().min(0),
    /** Tổng phiếu đã bỏ (yes+no+abstain) — aggregate. */
    totalVotes: z.number().int().min(0),
    /** Số thành viên đủ điều kiện bỏ phiếu (aggregate — KHÔNG danh sách). */
    eligibleCount: z.number().int().min(0),
  })
  .strict();

export type AdminConsensusDetail = z.infer<typeof adminConsensusDetailSchema>;
